package com.eladfinish.bible.contest.screens.completeVerse.usecases

import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndMakafUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveNikudAndTeamimAndMakafUseCase
import com.eladfinish.bible.contest.common.usecases.RemoveTeamimAndMakafUseCase
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.utils.withVav
import com.eladfinish.bible.contest.utils.withVavAndYud
import com.eladfinish.bible.contest.utils.withYud
import io.github.aakira.napier.Napier

class GetWordVersionsUseCase(
    private val removeNikudAndMakafUseCase: RemoveNikudAndMakafUseCase,
    private val removeTeamimAndMakafUseCase: RemoveTeamimAndMakafUseCase,
    private val removeNikudAndTeamimAndMakafUseCase: RemoveNikudAndTeamimAndMakafUseCase,
) {

    operator fun invoke(sourceWord: String): LinkedHashSet<String> {
        val wordVersions = linkedSetOf<String>()

        addBaseVersions(
            sourceWord = sourceWord,
            wordVersions = wordVersions,
        )

        val wordOnlyLetters = removeNikudAndTeamimAndMakafUseCase(sourceWord)
        addSpecialWordVersions(
            wordOnlyLetters = wordOnlyLetters,
            wordVersions = wordVersions,
        )

        val wordOnlyLettersAndNikud = removeTeamimAndMakafUseCase(sourceWord)
        addRegexBasedVersions(
            wordOnlyLettersAndNikud = wordOnlyLettersAndNikud,
            wordVersions = wordVersions,
        )

        Napier.d("wordVersions: $wordVersions")
        return LinkedHashSet(wordVersions)
    }

    private fun addBaseVersions(
        sourceWord: String,
        wordVersions: MutableSet<String>,
    ) {
        wordVersions.addAll(
            listOf(
                sourceWord,
                removeTeamimAndMakafUseCase(sourceWord),
                removeNikudAndTeamimAndMakafUseCase(sourceWord),
                removeNikudAndMakafUseCase(sourceWord.withVav()),
                removeNikudAndMakafUseCase(sourceWord.withYud()),
                removeNikudAndMakafUseCase(sourceWord.withVavAndYud()),
            ),
        )
    }

    private fun addSpecialWordVersions(
        wordOnlyLetters: String,
        wordVersions: MutableSet<String>,
    ) {
        for ((from, replacements) in SPECIAL_WORD_VERSIONS) {
            if (wordOnlyLetters.contains(from)) {
                replacements.forEach { replacement ->
                    wordVersions.add(
                        wordOnlyLetters.replaceRegex(
                            regex = from,
                            replacement = replacement,
                        ),
                    )
                }
            }
        }
    }

    private fun addRegexBasedVersions(
        wordOnlyLettersAndNikud: String,
        wordVersions: MutableSet<String>,
    ) {
        for (rule in REGEX_BASED_REPLACEMENTS) {
            val withPrefixRegex = "(.*?)${rule.pattern}$"
            if (wordOnlyLettersAndNikud.matches(withPrefixRegex.toRegex())) {
                rule.replacements.forEach { replacement ->
                    wordVersions.add(
                        wordOnlyLettersAndNikud
                            .replaceRegex(
                                regex = withPrefixRegex,
                                replacement = "$1$replacement",
                            )
                            .replaceRegex(
                                regex = StringConstants.Regex.NIKUD_CHARS,
                                replacement = "",
                            ),
                    )
                }
            }
        }
    }

    private data class RegexReplacementRule(
        val pattern: String,
        val replacements: List<String>,
    )

    private companion object {
        val SPECIAL_WORD_VERSIONS = mapOf(
            "אלהי" to listOf("אלוקי"),
            "יהוה" to listOf("ה'", "ה׳", "השם"),
            "שמאול" to listOf("שמאל"),
            "פלשתי" to listOf("פלישתי"),
            "ירושלם" to listOf("ירושלים"),
            "ירושלמה" to listOf("ירושלימה"),
            "דויד" to listOf("דוד"),
            "היתה" to listOf("הייתה"),
        )

        val REGEX_BASED_REPLACEMENTS = listOf(
            RegexReplacementRule(
                pattern = "הִוא",
                replacements = listOf("היא"),
            ),
            RegexReplacementRule(
                pattern = "יְ?הוִה",
                replacements = listOf("אלוהים", "אלוקים"),
            ),
            RegexReplacementRule(
                pattern = "אֲ?דֹנָי",
                replacements = listOf("ה'", "ה׳", "השם"),
            ),
        )
    }
}
